import React, { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Maximize2 } from "lucide-react";
import Sidebar from "./Sidebar";
import TopHeader from "./TopHeader";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import IncomingCallProvider from "@/components/common/IncomingCallProvider";
import CriticalCaseProvider from "@/components/common/CriticalCaseProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";

// Apply the user's saved account preferences (language/theme) once on login.
function PreferenceSync() {
  const { user } = useAuth();
  const { setLocale } = useI18n();
  const { setTheme } = useTheme();
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current || !user) return;
    const prefs = user.preferences || {};
    if (prefs.language) setLocale(prefs.language);
    if (prefs.theme) setTheme(prefs.theme);
    applied.current = true;
  }, [user, setLocale, setTheme]);

  return null;
}

// Floating launcher — opens the current Command Center screen in a chrome-free popup.
function PresentLauncher() {
  const { pathname } = useLocation();
  if (!pathname.startsWith("/command")) return null;
  const open = () => {
    const url = `${window.location.origin}/present${pathname}`;
    window.open(url, "_blank", "width=1680,height=950");
  };
  return (
    <button
      onClick={open}
      data-testid="present-launch"
      title="Open this dashboard in a separate presentation window"
      className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B7E8C] hover:bg-[#128C97] text-white shadow-lg transition"
    >
      <Maximize2 className="w-4 h-4" /> Present
    </button>
  );
}

function LayoutInner() {
  const { collapsed, mobileOpen, closeMobile } = useSidebar();
  return (
    <IncomingCallProvider>
      <CriticalCaseProvider>
      <PreferenceSync />
      <div className="min-h-screen hah-page-bg">
        <Sidebar />
        {mobileOpen && (
          <div
            className="fixed inset-0 z-[35] bg-black/50 lg:hidden"
            onClick={closeMobile}
            data-testid="sidebar-overlay"
          />
        )}
        <TopHeader />
        <main data-testid="main-content" className={`ml-0 ${collapsed ? "lg:ml-16" : "lg:ml-64"} pt-16 min-h-screen transition-all duration-200 overflow-x-hidden`}>
          <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-6 max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>
        <PresentLauncher />
      </div>
      </CriticalCaseProvider>
    </IncomingCallProvider>
  );
}

export default function AppLayout() {
  return <SidebarProvider><LayoutInner /></SidebarProvider>;
}
